import {
  Box,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material'
import { Suspense, useCallback, useEffect } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useHistory } from 'react-router-dom'
import { useQueryBarcodeExtractionRule } from './api/useQueryBarcodeExtractionRule'
import { useSaveBarcodeExtractionRule } from './api/useSaveBarcodeExtractionRule'
import { BarcodePreview } from './BarcodePreview'
import { getBarcodeEndPosition } from './barcodeRange'
import {
  BarcodeRangeForm,
  BarcodeSettingForm,
  parseBarcodeExtractionRule,
} from './form'
import { Header } from './Header'

const DEFAULT_RANGE = {
  startPosition: 5,
  length: 6,
}

export const BarcodeSettingPage = () => {
  const history = useHistory()
  const back = useCallback(() => history.push('/labelMasters'), [history])

  useEffect(() => {
    document.title = 'バーコード設定 | AIラベル検査 | カミナシ レポート'
  }, [])

  return (
    <Suspense
      fallback={
        <Box bgcolor="background.grayLight" height="100vh">
          <Header onBack={back} onSave={() => {}} canSave={false} />
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="calc(100vh - 64px)"
          >
            <CircularProgress />
          </Box>
        </Box>
      }
    >
      <BarcodeSettingContent onBack={back} />
    </Suspense>
  )
}

type ContentProps = {
  onBack: () => void
}

const BarcodeSettingContent = ({ onBack }: ContentProps) => {
  const { data } = useQueryBarcodeExtractionRule()
  const methods = useForm<BarcodeSettingForm>({
    defaultValues: { isEnabled: data !== null, ...(data ?? DEFAULT_RANGE) },
    mode: 'onChange',
  })
  const { mutate: save, isPending } = useSaveBarcodeExtractionRule()

  const isEnabled = methods.watch('isEnabled')
  const startPosition = methods.watch('startPosition')
  const length = methods.watch('length')
  const end = getBarcodeEndPosition(startPosition, length)

  // オフのときは非表示の開始位置・桁数を検証させないため、handleSubmit を通さない。
  // オンで不正な値のときは canSave で保存させない
  const handleSave = () => save(parseBarcodeExtractionRule(methods.getValues()))
  const canSave = !isPending && (!isEnabled || methods.formState.isValid)

  return (
    <FormProvider {...methods}>
      <Box bgcolor="background.grayLight" minHeight="100vh">
        <Header onBack={onBack} onSave={handleSave} canSave={canSave} />
        <Stack
          spacing={2}
          sx={{ mx: 'auto', maxWidth: '1000px', mt: 4, px: 3 }}
        >
          <Stack alignItems="flex-start">
            <Controller
              name="isEnabled"
              control={methods.control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(_, checked) => field.onChange(checked)}
                    />
                  }
                  label="読み取る範囲を指定する"
                />
              )}
            />
            <Typography variant="caption">
              ※この設定をオフにすると、バーコードの全桁を読み取ります
            </Typography>
          </Stack>
          {isEnabled && (
            <>
              <BarcodeRangeForm />
              <BarcodePreview startPosition={startPosition} endPosition={end} />
            </>
          )}
        </Stack>
      </Box>
    </FormProvider>
  )
}
